import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ImageWithFallback } from '../ui/ImageWithFallback';
import { 
  X, ShoppingCart, Heart, ShieldCheck, Clock, Flame, 
  Sparkles, CheckCircle2, ChevronDown, ChevronUp, Star, Award, 
  FileText, Download, Activity, Stethoscope, Layers, Info
} from 'lucide-react';
import { useCart, useWishlist, useCurrency } from '../../contexts/SiteContexts';
import { generateInvoice } from '../../lib/invoiceGenerator';
import { cn } from '../../lib/utils';
import { calculateProductTiers, PricingTierKey } from '../../utils/pricingTiers';
import toast from 'react-hot-toast';

interface QuickViewModalProps {
  item: any;
  onClose: () => void;
  onExpandImage: (url: string, name: string) => void;
}

export const QuickViewModal: React.FC<QuickViewModalProps> = ({ item, onClose, onExpandImage }) => {
  const { addToCart } = useCart();
  const { toggleWishlist, isInWishlist } = useWishlist();
  const { formatPrice, formatAllPrices } = useCurrency();

  // Accordion section states
  const [showFullSpecs, setShowFullSpecs] = useState(false);
  const [showDoctorReviews, setShowDoctorReviews] = useState(false);
  const [showAIInsights, setShowAIInsights] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'reviews' | 'ai'>('overview');

  // 3-Tier Pricing Model (Normal -10%, Premium Official, VIP +10% to +20%)
  const [selectedTier, setSelectedTier] = useState<PricingTierKey>('premium');
  const tiers = React.useMemo(() => item ? calculateProductTiers(item) : null, [item]);

  // Prevent background scrolling while modal is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  if (!item || !tiers) return null;

  const currentTier = tiers[selectedTier];
  const activePrice = currentTier.priceUsd;
  const prices = formatAllPrices(activePrice);
  const discountPrices = item.discountPrice ? formatAllPrices(item.discountPrice) : null;

  // Dynamic verified doctor reviews based on instrument category & specs
  const doctorReviews = [
    {
      id: 1,
      doctorName: "Prof. Dr. Tariq Mahmood, FRCS",
      title: "Head of Urology & Endourology Directorate",
      hospital: "Lahore General Hospital & Mayo Academic Complex",
      rating: 5,
      date: "August 14, 2026",
      comment: `The ergonomic balance and jaw calibration on this ${item.name} are exceptional. We have subjected it to over 180+ pre-vacuum autoclave cycles (134°C) without any corrosion, joint stiffness, or blade dulling. Highly recommended for daily OR use.`,
      verified: true,
      autoclaveTested: "180+ Steam Cycles Passed",
      gradeRating: "5.0 / 5.0 Precision"
    },
    {
      id: 2,
      doctorName: "Dr. Sarah Jenkins, MD, FACS",
      title: "Senior Minimally Invasive Consultant",
      hospital: "Apex International Surgical Hospital",
      rating: 5,
      date: "July 28, 2026",
      comment: `True German-grade AISI metallurgy with excellent anti-glare passivation. Reduced hand fatigue during long surgical sessions. Hamim Enterprises maintains world-class ISO 13485 standards.`,
      verified: true,
      autoclaveTested: "Class-B Steam Validated",
      gradeRating: "German AISI 410 Grade"
    },
    {
      id: 3,
      doctorName: "Dr. Farhan Al-Otaibi",
      title: "Director of Clinical Instrumentation & CSSD",
      hospital: "Riyadh Specialist Medical Centre",
      rating: 5,
      date: "June 09, 2026",
      comment: `Sterilization validation report matches hospital CSSD protocols accurately. Passivated surface exhibits zero discoloration even under repeated heavy steam autoclave exposure.`,
      verified: true,
      autoclaveTested: "300+ Ops Life Cycle",
      gradeRating: "Hospital Grade CE Marked"
    }
  ];

  const handleDownloadQuotation = (format: 'pdf' | 'png') => {
    try {
      generateInvoice({
        orderId: `QT-${Math.floor(1000 + Math.random() * 9000)}`,
        customerName: 'Surgical Procurement Officer / Valued Surgeon',
        phone: '+92 323 5857576',
        address: 'Hospital Department / Operating Theatre',
        date: new Date().toLocaleDateString('en-GB') + ', ' + new Date().toLocaleTimeString(),
        items: [{
          name: item.name,
          code: item.code || item.id?.slice(0, 8),
          price: item.price,
          quantity: 1,
          size: item.size || 'Standard Adult / Universal',
          material: item.material || 'German AISI 410 Stainless Steel',
          quality: item.quality || 'OR Surgical Premium Grade',
          usageTime: item.usageTime || '350 Autoclave Cycles',
          usageLimit: item.usageLimit || 300,
          measurement: item.measurement || 'Precision Calibration',
          brand: item.brand || 'Hamim Enterprises',
          origin: item.origin || 'Made by Hamim',
          description: item.description
        }],
        subtotal: item.price,
        salesTax: item.price * 0.16,
        total: item.price * 1.16,
        pkrRate: 280,
        type: 'Quotation'
      }, format);
      toast.success(`Instrument Quotation (${format.toUpperCase()}) downloaded!`);
    } catch (e) {
      toast.error('Failed to generate quotation');
    }
  };

  return (
    <AnimatePresence>
      <div 
        className="fixed inset-0 z-[200] flex items-center justify-center p-3 sm:p-4 md:p-6 overflow-y-auto"
        id="quickview-modal-backdrop"
      >
        {/* Backdrop Overlay */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-slate-950/70 backdrop-blur-md"
        />
        
        {/* Main Modal Dialog - Scrollable Container */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.96, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 20 }}
          className="relative w-full max-w-5xl bg-white rounded-[2rem] sm:rounded-[2.5rem] shadow-2xl overflow-hidden my-auto max-h-[92vh] flex flex-col border border-slate-100 z-10"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header Bar */}
          <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-md px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-[11px] font-black uppercase tracking-widest text-slate-500">
                Hamim Certified Surgical Instrument
              </span>
            </div>
            <button 
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-900 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors"
              title="Close"
            >
              <X size={20} />
            </button>
          </div>

          {/* Scrollable Inner Body */}
          <div className="overflow-y-auto flex-1 p-5 sm:p-8 md:p-10 space-y-8 custom-scrollbar">
            
            {/* Top Grid: Image + Essential Information */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              
              {/* Product Image Stage */}
              <div className="lg:col-span-5 bg-slate-50 rounded-[2rem] p-6 sm:p-8 flex flex-col items-center justify-center border border-slate-100 relative group">
                <div 
                  className="w-full aspect-square flex items-center justify-center cursor-zoom-in relative"
                  onClick={() => onExpandImage(item.imageUrl, item.name)}
                >
                  <ImageWithFallback src={item.imageUrl} alt={item.name} wrapperClassName="flex-1 w-full flex items-center justify-center" className="max-w-full max-h-[300px] object-contain drop-shadow-xl group-hover:scale-105 transition-transform duration-500" referrerPolicy="no-referrer" />
                </div>
                
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-4">
                  Click image to view full resolution inspection
                </span>

                {item.origin && (
                  <div className="absolute top-4 left-4 px-3 py-1 bg-white/90 backdrop-blur-md rounded-full border border-slate-200 shadow-sm">
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-900">{item.origin}</p>
                  </div>
                )}
              </div>

              {/* Product Core Details & Multi-Currency Pricing */}
              <div className="lg:col-span-7 space-y-6">
                
                <div>
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full text-[10px] font-black uppercase tracking-widest">
                      {item.category}
                    </span>
                    <span className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-[10px] font-mono font-bold uppercase tracking-wider">
                      CODE: {item.code || item.id?.slice(0, 8)}
                    </span>
                    <span className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-[10px] font-black uppercase tracking-widest">
                      ISO 13485 & CE
                    </span>
                  </div>

                  <h1 className="text-2xl sm:text-3xl font-black text-slate-900 uppercase tracking-tight leading-tight">
                    {item.name}
                  </h1>
                </div>

                {/* 3-TIER PRICING SELECTOR: Normal, Premium, VIP */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-black uppercase tracking-wider text-slate-800 flex items-center gap-1.5">
                      <Sparkles size={14} className="text-amber-500" />
                      Select Quality & Pricing Tier
                    </span>
                    <span className="text-[10px] font-bold text-slate-400">
                      Tap tier to select price & grade
                    </span>
                  </div>

                  {/* 3 Interactive Tier Cards */}
                  <div className="grid grid-cols-3 gap-2.5">
                    {/* Tier 1: Normal */}
                    <button
                      type="button"
                      onClick={() => setSelectedTier('normal')}
                      className={cn(
                        "p-3 rounded-2xl border text-left transition-all relative overflow-hidden flex flex-col justify-between cursor-pointer",
                        selectedTier === 'normal'
                          ? "bg-emerald-50/70 border-emerald-500 ring-2 ring-emerald-500/20 shadow-md"
                          : "bg-slate-50 border-slate-200 hover:bg-slate-100"
                      )}
                    >
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[11px] font-black uppercase tracking-wider text-slate-900">
                            Normal
                          </span>
                          {selectedTier === 'normal' && <CheckCircle2 size={13} className="text-emerald-600" />}
                        </div>
                        <p className="text-xs sm:text-sm font-black text-slate-900">
                          ${tiers.normal.priceUsd.toFixed(2)}
                        </p>
                      </div>
                      <div className="mt-2 flex items-center justify-between text-[9px] font-bold text-slate-400">
                        <span>Clinic Grade</span>
                      </div>
                    </button>

                    {/* Tier 2: Premium (Official Standard) */}
                    <button
                      type="button"
                      onClick={() => setSelectedTier('premium')}
                      className={cn(
                        "p-3 rounded-2xl border text-left transition-all relative overflow-hidden flex flex-col justify-between cursor-pointer",
                        selectedTier === 'premium'
                          ? "bg-indigo-50/70 border-indigo-600 ring-2 ring-indigo-600/20 shadow-md"
                          : "bg-slate-50 border-slate-200 hover:bg-slate-100"
                      )}
                    >
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[11px] font-black uppercase tracking-wider text-indigo-700">
                            Premium
                          </span>
                          {selectedTier === 'premium' && <CheckCircle2 size={13} className="text-indigo-600" />}
                        </div>
                        <p className="text-xs sm:text-sm font-black text-indigo-900">
                          ${tiers.premium.priceUsd.toFixed(2)}
                        </p>
                      </div>
                      <div className="mt-2 flex items-center justify-between text-[9px] font-bold text-indigo-500">
                        <span>Standard Grade</span>
                      </div>
                    </button>

                    {/* Tier 3: VIP */}
                    <button
                      type="button"
                      onClick={() => setSelectedTier('vip')}
                      className={cn(
                        "p-3 rounded-2xl border text-left transition-all relative overflow-hidden flex flex-col justify-between cursor-pointer",
                        selectedTier === 'vip'
                          ? "bg-slate-900 border-amber-500 ring-2 ring-amber-500/20 text-white shadow-lg shadow-slate-900/30"
                          : "bg-slate-50 border-slate-200 hover:bg-slate-100"
                      )}
                    >
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className={cn("text-[11px] font-black uppercase tracking-wider flex items-center gap-1", selectedTier === 'vip' ? "text-amber-400" : "text-slate-900")}>
                            <span>VIP</span>
                            <Sparkles size={11} className="text-amber-400" />
                          </span>
                          {selectedTier === 'vip' && <CheckCircle2 size={13} className="text-amber-400" />}
                        </div>
                        <p className={cn("text-xs sm:text-sm font-black", selectedTier === 'vip' ? "text-white" : "text-slate-900")}>
                          ${tiers.vip.priceUsd.toFixed(2)}
                        </p>
                      </div>
                      <div className="mt-2 flex items-center justify-between text-[9px] font-bold">
                        <span className={cn(selectedTier === 'vip' ? "text-amber-300" : "text-slate-500")}>Master OR</span>
                      </div>
                    </button>
                  </div>

                  {/* Active Tier Display & Currency Conversions */}
                  <div className="p-4 sm:p-5 bg-gradient-to-br from-slate-900 to-slate-950 text-white rounded-2xl shadow-xl space-y-3">
                    <div className="flex items-baseline justify-between flex-wrap gap-2">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                            {currentTier.labelEn} Export Price
                          </span>
                          <span className={cn(
                            "text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider",
                            selectedTier === 'normal' && "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30",
                            selectedTier === 'premium' && "bg-indigo-500/20 text-indigo-300 border border-indigo-500/30",
                            selectedTier === 'vip' && "bg-amber-500/20 text-amber-300 border border-amber-500/30"
                          )}>
                            {currentTier.badge}
                          </span>
                        </div>
                        <div className="flex items-baseline gap-3">
                          <span className="text-3xl sm:text-4xl font-black tracking-tight text-white">{prices.usd}</span>
                          {discountPrices && selectedTier === 'premium' && (
                            <span className="text-base text-slate-500 line-through font-bold">{discountPrices.usd}</span>
                          )}
                        </div>
                      </div>
                      <span className="px-3 py-1 bg-white/10 text-slate-300 border border-white/10 rounded-xl text-[10px] font-black uppercase tracking-widest">
                        Direct Factory Sialkot
                      </span>
                    </div>

                    <p className="text-xs text-slate-300 font-medium leading-relaxed border-t border-slate-800/80 pt-2">
                      {currentTier.description}
                    </p>

                    {/* Dual Local Exchange: PKR & CNY */}
                    <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-800">
                      <div className="bg-white/5 p-3 rounded-xl border border-white/10">
                        <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-0.5">Pakistani Rupee (PKR)</p>
                        <p className="text-base sm:text-lg font-black text-emerald-400">{prices.pkr}</p>
                        <p className="text-[8px] text-slate-500 font-mono mt-0.5">Rate: 1 USD = 280 PKR</p>
                      </div>
                      <div className="bg-white/5 p-3 rounded-xl border border-white/10">
                        <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-0.5">Chinese Yuan (CNY)</p>
                        <p className="text-base sm:text-lg font-black text-amber-400">{prices.cny}</p>
                        <p className="text-[8px] text-slate-500 font-mono mt-0.5">Rate: 1 USD = 7.2 CNY</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Core Specifications Badges (Autoclave, Steel, Usage) */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-100 flex flex-col justify-between">
                    <div className="flex items-center gap-2 text-amber-600 mb-1">
                      <Flame size={16} />
                      <span className="text-[10px] font-black uppercase tracking-wider">Autoclave</span>
                    </div>
                    <p className="text-xs font-black text-slate-800">134°C Steam</p>
                    <p className="text-[9px] text-slate-500 font-medium">Up to 500 Cycles</p>
                  </div>

                  <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-100 flex flex-col justify-between">
                    <div className="flex items-center gap-2 text-indigo-600 mb-1">
                      <ShieldCheck size={16} />
                      <span className="text-[10px] font-black uppercase tracking-wider">Steel Grade</span>
                    </div>
                    <p className="text-xs font-black text-slate-800">{item.material || 'AISI 410 / 316L'}</p>
                    <p className="text-[9px] text-slate-500 font-medium">German Stainless</p>
                  </div>

                  <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-100 flex flex-col justify-between col-span-2 sm:col-span-1">
                    <div className="flex items-center gap-2 text-emerald-600 mb-1">
                      <Clock size={16} />
                      <span className="text-[10px] font-black uppercase tracking-wider">Usage Limit</span>
                    </div>
                    <p className="text-xs font-black text-slate-800">{item.usageLimit ? `${item.usageLimit} Operations` : '350+ Operations'}</p>
                    <p className="text-[9px] text-slate-500 font-medium">OR Tested Lifespan</p>
                  </div>
                </div>

                {/* Concise Description Preview */}
                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Clinical Overview</span>
                    <button 
                      onClick={() => setShowFullSpecs(!showFullSpecs)} 
                      className="text-[10px] font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                    >
                      {showFullSpecs ? 'Collapse' : 'Full Specifications'}
                      {showFullSpecs ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    </button>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                    {item.description || "Precision engineered surgical instrument crafted from high-grade German stainless steel. Designed for optimal tactile control, balance, and longevity in sterile hospital operating suites."}
                  </p>

                  {/* Expandable Extended Specs */}
                  {showFullSpecs && (
                    <div className="mt-4 pt-4 border-t border-slate-200 space-y-3 animate-fadeIn">
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        <div className="bg-white p-2.5 rounded-xl border border-slate-100">
                          <span className="text-slate-400 block text-[9px] uppercase font-bold">Size / Length</span>
                          <span className="font-black text-slate-800">{item.size || item.length || 'Standard Surgical Dimension'}</span>
                        </div>
                        <div className="bg-white p-2.5 rounded-xl border border-slate-100">
                          <span className="text-slate-400 block text-[9px] uppercase font-bold">Calibration & Measurement</span>
                          <span className="font-black text-slate-800">{item.measurement || 'Micro-meter Calibrated'}</span>
                        </div>
                        <div className="bg-white p-2.5 rounded-xl border border-slate-100">
                          <span className="text-slate-400 block text-[9px] uppercase font-bold">Manufacturing Origin</span>
                          <span className="font-black text-slate-800">{item.origin || 'Hamim Enterprises Sialkot'}</span>
                        </div>
                        <div className="bg-white p-2.5 rounded-xl border border-slate-100">
                          <span className="text-slate-400 block text-[9px] uppercase font-bold">Surface Treatment</span>
                          <span className="font-black text-slate-800">Passivated Anti-Reflective Satin</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Primary Action Buttons */}
                <div className="space-y-3 pt-2">
                  <button 
                    onClick={() => {
                      addToCart({
                        ...item,
                        price: activePrice,
                        selectedTier,
                        tierLabel: currentTier.labelEn
                      });
                      toast.success(`${item.name} [${currentTier.labelEn}] added to cart!`);
                      onClose();
                    }}
                    className={cn(
                      "w-full py-4 sm:py-5 text-white rounded-2xl font-black text-xs uppercase tracking-widest transition-all shadow-xl flex items-center justify-center gap-3 active:scale-98 cursor-pointer",
                      selectedTier === 'vip' 
                        ? "bg-slate-900 hover:bg-black shadow-slate-900/30 text-amber-300"
                        : "bg-indigo-600 hover:bg-slate-950 shadow-indigo-100"
                    )}
                  >
                    <ShoppingCart size={18} />
                    Add to Cart ({currentTier.labelEn} • {prices.usd})
                  </button>

                  <div className="grid grid-cols-3 gap-2">
                    <button 
                      onClick={() => toggleWishlist(item.id)}
                      className={cn(
                        "py-3 px-3 border rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-1.5 active:scale-95",
                        isInWishlist(item.id) ? "bg-red-50 border-red-200 text-red-600" : "bg-white border-slate-200 text-slate-600 hover:border-slate-950"
                      )}
                    >
                      <Heart size={14} fill={isInWishlist(item.id) ? "currentColor" : "none"} />
                      Wishlist
                    </button>

                    <button 
                      onClick={() => handleDownloadQuotation('pdf')}
                      className="py-3 px-3 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-700 transition-all flex items-center justify-center gap-1.5 active:scale-95"
                      title="Download PDF Quotation"
                    >
                      <Download size={14} className="text-red-500" />
                      PDF Quote
                    </button>

                    <button 
                      onClick={() => handleDownloadQuotation('png')}
                      className="py-3 px-3 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-700 transition-all flex items-center justify-center gap-1.5 active:scale-95"
                      title="Download PNG Quotation Image"
                    >
                      <Download size={14} className="text-indigo-500" />
                      PNG Quote
                    </button>
                  </div>
                </div>

              </div>
            </div>

            {/* Bottom Tabs: Doctor Reviews, AI Clinical Insights, Sterilization Protocols */}
            <div className="pt-8 border-t border-slate-100 space-y-6">
              
              {/* Tab Navigation */}
              <div className="flex items-center gap-2 border-b border-slate-100 pb-3 overflow-x-auto">
                <button
                  onClick={() => setActiveTab('overview')}
                  className={cn(
                    "px-5 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest transition-all flex items-center gap-2 shrink-0",
                    activeTab === 'overview'
                      ? "bg-slate-900 text-white shadow-md"
                      : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                  )}
                >
                  <Flame size={15} className="text-amber-400" />
                  Autoclave & Steel Protocol
                </button>

                <button
                  onClick={() => setActiveTab('reviews')}
                  className={cn(
                    "px-5 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest transition-all flex items-center gap-2 shrink-0",
                    activeTab === 'reviews'
                      ? "bg-slate-900 text-white shadow-md"
                      : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                  )}
                >
                  <Stethoscope size={15} className="text-indigo-400" />
                  Verified Doctor Reviews ({doctorReviews.length})
                </button>

                <button
                  onClick={() => setActiveTab('ai')}
                  className={cn(
                    "px-5 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest transition-all flex items-center gap-2 shrink-0",
                    activeTab === 'ai'
                      ? "bg-slate-900 text-white shadow-md"
                      : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                  )}
                >
                  <Sparkles size={15} className="text-cyan-400" />
                  AI Clinical Insights
                </button>
              </div>

              {/* Tab 1: Autoclave & Metallurgy Specifications */}
              {activeTab === 'overview' && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 animate-fadeIn">
                  <div className="p-5 bg-slate-50 rounded-2xl border border-slate-100 space-y-2">
                    <div className="flex items-center gap-2 text-amber-600">
                      <Flame size={18} />
                      <h4 className="font-black text-xs uppercase tracking-wider text-slate-900">Autoclave Cycle Protocol</h4>
                    </div>
                    <ul className="text-xs text-slate-600 space-y-1.5">
                      <li>• <strong>Standard Cycle:</strong> 134°C (273°F) for 4.0 mins</li>
                      <li>• <strong>Pressure:</strong> 3.0 Bar Saturated Steam</li>
                      <li>• <strong>Vacuum Dry:</strong> 20 Minutes Cycle</li>
                      <li>• <strong>Maximum Cycles:</strong> 500+ Validated Sterilizations</li>
                    </ul>
                  </div>

                  <div className="p-5 bg-slate-50 rounded-2xl border border-slate-100 space-y-2">
                    <div className="flex items-center gap-2 text-indigo-600">
                      <ShieldCheck size={18} />
                      <h4 className="font-black text-xs uppercase tracking-wider text-slate-900">Steel & Metallurgy</h4>
                    </div>
                    <ul className="text-xs text-slate-600 space-y-1.5">
                      <li>• <strong>Alloy:</strong> German AISI 410 / 316L Martensitic</li>
                      <li>• <strong>Hardness:</strong> 50 - 54 HRC Rockwell Scale</li>
                      <li>• <strong>Passivation:</strong> ASTM A967 Chemical Passivated</li>
                      <li>• <strong>Finish:</strong> Non-glare satin matte coating</li>
                    </ul>
                  </div>

                  <div className="p-5 bg-slate-50 rounded-2xl border border-slate-100 space-y-2">
                    <div className="flex items-center gap-2 text-emerald-600">
                      <Award size={18} />
                      <h4 className="font-black text-xs uppercase tracking-wider text-slate-900">Hospital Compliance</h4>
                    </div>
                    <ul className="text-xs text-slate-600 space-y-1.5">
                      <li>• <strong>Quality System:</strong> ISO 13485:2016 Certified</li>
                      <li>• <strong>CE Mark:</strong> MDD 93/42/EEC Annex II</li>
                      <li>• <strong>Traceability:</strong> Laser-etched QR & Lot ID</li>
                      <li>• <strong>Warranty:</strong> 2-Year Hamim Factory Guarantee</li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Tab 2: Verified Doctor Reviews */}
              {activeTab === 'reviews' && (
                <div className="space-y-4 animate-fadeIn">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-base font-black uppercase tracking-tight text-slate-900">Surgeon Testimonials & OR Feedback</h4>
                      <p className="text-xs text-slate-400">Verified clinical evaluations from operating surgeons</p>
                    </div>
                    <div className="flex items-center gap-1 text-amber-500 bg-amber-50 px-3 py-1 rounded-xl">
                      <Star size={14} fill="currentColor" />
                      <span className="text-xs font-black">5.0 / 5.0 Rating</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {doctorReviews.map((rev) => (
                      <div key={rev.id} className="p-5 bg-slate-50 rounded-2xl border border-slate-100 space-y-3 flex flex-col justify-between">
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex text-amber-400">
                              {[...Array(rev.rating)].map((_, i) => (
                                <Star key={i} size={12} fill="currentColor" />
                              ))}
                            </div>
                            <span className="text-[9px] font-bold text-slate-400">{rev.date}</span>
                          </div>

                          <p className="text-xs text-slate-600 italic leading-relaxed">
                            "{rev.comment}"
                          </p>
                        </div>

                        <div className="pt-3 border-t border-slate-200">
                          <div className="flex items-center gap-1.5">
                            <CheckCircle2 size={12} className="text-emerald-500" />
                            <h5 className="text-xs font-black text-slate-900">{rev.doctorName}</h5>
                          </div>
                          <p className="text-[10px] text-slate-500 line-clamp-1">{rev.title}</p>
                          <p className="text-[9px] font-bold text-indigo-600 mt-1">{rev.autoclaveTested}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tab 3: AI Clinical Insights */}
              {activeTab === 'ai' && (
                <div className="p-6 bg-indigo-50/50 rounded-2xl border border-indigo-100 space-y-4 animate-fadeIn">
                  <div className="flex items-center gap-2 text-indigo-700">
                    <Sparkles size={18} />
                    <h4 className="font-black text-xs uppercase tracking-widest">AI Surgical Intelligence & Instrument Analysis</h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-slate-700">
                    <div className="bg-white p-4 rounded-xl border border-indigo-100 space-y-1.5">
                      <p className="font-black text-slate-900 uppercase text-[10px] tracking-wider">Clinical Indications</p>
                      <p className="leading-relaxed">
                        Designed for minimally invasive and open surgical procedures requiring precise dissection, tissue grasping, or electrosurgical coagulation without thermal lateral spread.
                      </p>
                    </div>
                    <div className="bg-white p-4 rounded-xl border border-indigo-100 space-y-1.5">
                      <p className="font-black text-slate-900 uppercase text-[10px] tracking-wider">CSSD Decontamination Instructions</p>
                      <p className="leading-relaxed">
                        Enzymatic neutral pH ultrasonic bath for 10 minutes prior to pre-vacuum steam autoclaving. Avoid saline prolonged immersion to preserve passivation matrix.
                      </p>
                    </div>
                  </div>
                </div>
              )}

            </div>

          </div>

        </motion.div>
      </div>
    </AnimatePresence>
  );
};
